import React, { useState, useEffect } from 'react';
import { Shield, Lock, CheckCircle, AlertCircle, Key, Eye, EyeOff } from 'lucide-react';
import { PasswordStrengthIndicator } from './RecoveryPage';
import logo from '../assets/logo.svg';
import { NEU } from '../neuStyles';

/**
 * SecuritySetupPage - shown after login when:
 * 1. User needs to set up security questions (first login or after admin reset)
 * 2. User needs to change their password (admin forced reset)
 * 3. Admin/physician user must set up TOTP before accessing the application
 */
function SecuritySetupPage({ user, requiresPasswordChange, requiresSecuritySetup, requiresTOTPSetup, onComplete }) {
  const [phase, setPhase] = useState(
    requiresPasswordChange ? 'password' : requiresSecuritySetup ? 'questions' : 'totp'
  );

  function handlePasswordDone() {
    if (requiresSecuritySetup) {
      setPhase('questions');
    } else if (requiresTOTPSetup) {
      setPhase('totp');
    } else {
      onComplete();
    }
  }

  function handleQuestionsDone() {
    if (requiresTOTPSetup) {
      setPhase('totp');
    } else {
      onComplete();
    }
  }

  function handleTOTPDone() {
    onComplete();
  }

  if (phase === 'password') {
    return <ForcePasswordChange user={user} onDone={handlePasswordDone} />;
  }

  if (phase === 'questions') {
    return <SetupSecurityQuestions user={user} onDone={handleQuestionsDone} />;
  }

  // phase === 'totp'
  return <ForceTOTPSetup user={user} onDone={handleTOTPDone} />;
}

// ─── Force Password Change ─────────────────────────────────────────────────

function ForcePasswordChange({ user, onDone }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!currentPassword) { setError('Please enter your current password'); return; }
    if (!newPassword) { setError('Please enter a new password'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 12) { setError('Password must be at least 12 characters'); return; }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.changePassword(currentPassword, newPassword);
      if (result.error) {
        setError(result.message);
      } else {
        onDone();
      }
    } catch (err) {
      setError('Password change failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 480 }}>
        <div className="login-header">
          <img src={logo} alt="ZignaGenix" className="login-logo" />
          <h1 className="login-title">Password Change Required</h1>
          <p className="login-subtitle">
            Your administrator has required you to change your password before continuing.
          </p>
        </div>

        <form className="login-form" onSubmit={handleSubmit} autoComplete="off">
          {error && <div className="login-error"><AlertCircle size={16} /><span>{error}</span></div>}

          <div className="login-field">
            <label className="login-label"><Lock size={14} /> Current Password</label>
            <div className="login-password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'} className="login-input"
                value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password" autoFocus disabled={loading}
                autoComplete="current-password"
              />
              <button type="button" className="login-password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="login-field">
            <label className="login-label"><Key size={14} /> New Password</label>
            <div className="login-password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'} className="login-input"
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 12 chars, upper+lower+number+special" disabled={loading}
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="login-field">
            <label className="login-label"><Key size={14} /> Confirm New Password</label>
            <div className="login-password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'} className="login-input"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password" disabled={loading}
                autoComplete="new-password"
              />
            </div>
          </div>

          <PasswordStrengthIndicator password={newPassword} />

          <div style={{ ...NEU.inset, padding: '10px 14px', fontSize: 12, color: '#e65100', marginBottom: 8 }}>
            <strong>Password Policy:</strong> Cannot reuse any of your last 24 passwords. Must include uppercase, lowercase, number, and special character.
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Changing Password...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Setup Security Questions ──────────────────────────────────────────────

function SetupSecurityQuestions({ user, onDone }) {
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selections, setSelections] = useState([
    { questionText: '', answer: '' },
    { questionText: '', answer: '' },
    { questionText: '', answer: '' },
  ]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    async function loadQuestions() {
      try {
        const questions = await window.zignagenixAPI.getSecurityQuestionsList();
        setAvailableQuestions(questions);
      } catch (err) {
        console.error('Failed to load security questions:', err);
      }
    }
    loadQuestions();
  }, []);

  function updateSelection(index, field, value) {
    const updated = [...selections];
    updated[index] = { ...updated[index], [field]: value };
    setSelections(updated);
  }

  // Get questions already selected by other dropdowns
  function getUsedQuestions(excludeIndex) {
    return selections
      .filter((_, i) => i !== excludeIndex)
      .map((s) => s.questionText)
      .filter(Boolean);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    // Validate all 3 questions are selected and answered
    for (let i = 0; i < 3; i++) {
      if (!selections[i].questionText) {
        setError(`Please select security question ${i + 1}`);
        return;
      }
      if (!selections[i].answer.trim() || selections[i].answer.trim().length < 2) {
        setError(`Please provide an answer for question ${i + 1} (at least 2 characters)`);
        return;
      }
    }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.setupSecurityQuestions(selections);
      if (result.error) {
        setError(result.message);
      } else {
        setSuccess(true);
        setTimeout(() => onDone(), 1500);
      }
    } catch (err) {
      setError('Failed to save security questions. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 520 }}>
        <div className="login-header">
          <img src={logo} alt="ZignaGenix" className="login-logo" />
          <h1 className="login-title">Security Questions Setup</h1>
          <p className="login-subtitle">
            Set up 3 security questions for account recovery. These are required for HIPAA compliance.
          </p>
        </div>

        {success ? (
          <div style={{ padding: '0 24px 24px', textAlign: 'center' }}>
            <div style={{ ...NEU.cardLg, background: '#e8f5e9', padding: 20 }}>
              <CheckCircle size={32} color="#43a047" style={{ marginBottom: 8 }} />
              <p style={{ fontSize: 16, color: '#2e7d32', fontWeight: 600 }}>Security Questions Saved</p>
              <p style={{ fontSize: 13, color: '#6C757D', marginTop: 8 }}>Redirecting to the application...</p>
            </div>
          </div>
        ) : (
          <form className="login-form" onSubmit={handleSubmit} style={{ maxHeight: '65vh', overflowY: 'auto' }}>
            {error && <div className="login-error"><AlertCircle size={16} /><span>{error}</span></div>}

            {[0, 1, 2].map((i) => {
              const used = getUsedQuestions(i);
              return (
                <div key={i} style={{ marginBottom: 16, ...NEU.cardSm, padding: '12px' }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#212529', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <Shield size={14} color="#2E8B98" /> Question {i + 1}
                  </label>
                  <select
                    className="login-input"
                    style={{ marginBottom: 8, fontSize: 13 }}
                    value={selections[i].questionText}
                    onChange={(e) => updateSelection(i, 'questionText', e.target.value)}
                    disabled={loading}
                  >
                    <option value="">-- Select a security question --</option>
                    {availableQuestions
                      .filter((q) => !used.includes(q))
                      .map((q) => (
                        <option key={q} value={q}>{q}</option>
                      ))
                    }
                  </select>
                  <input
                    type="text" className="login-input"
                    value={selections[i].answer}
                    onChange={(e) => updateSelection(i, 'answer', e.target.value)}
                    placeholder="Your answer (case-insensitive)"
                    disabled={loading}
                    style={{ fontSize: 13 }}
                  />
                </div>
              );
            })}

            <div style={{ ...NEU.inset, padding: '10px 14px', fontSize: 12, color: '#2E8B98', marginBottom: 8 }}>
              <strong>Important:</strong> Remember your answers exactly. They are used for username recovery and password reset. Answers are not case-sensitive.
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Saving...' : 'Save Security Questions'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Force TOTP Setup ──────────────────────────────────────────────────────
// Shown to admin/physician users who haven't configured two-factor authentication.
// They must complete TOTP setup before they can access the application.

function ForceTOTPSetup({ user, onDone }) {
  const [step, setStep] = useState('intro'); // intro | scan | verify
  const [totpData, setTotpData] = useState(null);
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  async function handleGenerate() {
    setLoading(true);
    setError('');
    try {
      const result = await window.zignagenixAPI.totpGenerate();
      if (result.error) {
        setError(result.message);
      } else {
        setTotpData(result);
        setStep('scan');
      }
    } catch (err) {
      setError('Failed to generate TOTP secret. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    setError('');
    const cleanToken = token.replace(/\s/g, '');
    if (!cleanToken || cleanToken.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const result = await window.zignagenixAPI.totpVerifyAndEnable(cleanToken);
      if (result.error) {
        setError(result.message);
      } else {
        onDone();
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function copySecret() {
    if (totpData?.secret) {
      navigator.clipboard.writeText(totpData.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 480 }}>
        <div className="login-header">
          <h1 className="login-title" style={{ fontSize: 22 }}>Two-Factor Authentication Required</h1>
          <p className="login-subtitle">
            Your role requires two-factor authentication to access this system.
          </p>
        </div>

        <div style={{ padding: '0 24px 24px' }}>
          {error && (
            <div className="login-error" style={{ marginBottom: 16 }}>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {step === 'intro' && (
            <div>
              <div style={{ ...NEU.inset, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#2E8B98' }}>
                <strong>Why is this required?</strong> Admin and physician accounts have access to protected health information (PHI). HIPAA §164.312(d) requires strong authentication for accounts with elevated access.
              </div>
              <p style={{ fontSize: 14, color: '#374151', marginBottom: 16 }}>
                You'll need an authenticator app (Google Authenticator, Authy, or similar) installed on your phone before proceeding.
              </p>
              <button className="login-button" onClick={handleGenerate} disabled={loading}>
                {loading ? 'Setting up...' : 'Set Up Authenticator App'}
              </button>
            </div>
          )}

          {step === 'scan' && totpData && (
            <div>
              <p style={{ fontSize: 13, color: '#374151', marginBottom: 16 }}>
                Scan this QR code (or enter the secret manually) in your authenticator app:
              </p>
              <div style={{ ...NEU.inset, padding: 16, marginBottom: 16, textAlign: 'center' }}>
                <p style={{ fontSize: 11, color: '#6C757D', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Manual entry key</p>
                <code style={{ fontSize: 16, letterSpacing: '0.15em', color: '#1f2937', fontWeight: 600 }}>
                  {totpData.secret?.match(/.{1,4}/g)?.join(' ')}
                </code>
                <button
                  type="button"
                  onClick={copySecret}
                  style={{ display: 'block', margin: '8px auto 0', fontSize: 12, color: '#2E8B98', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {copied ? '✓ Copied' : 'Copy to clipboard'}
                </button>
              </div>
              <div style={{ ...NEU.inset, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#e65100' }}>
                <strong>Save your backup codes:</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {(totpData.backupCodes || []).map((code, i) => (
                    <code key={i} style={{ ...NEU.input, padding: '2px 8px', fontSize: 12 }}>{code}</code>
                  ))}
                </div>
                <p style={{ marginTop: 8, marginBottom: 0 }}>Store these somewhere safe. Each can be used once if you lose your phone.</p>
              </div>
              <button className="login-button" onClick={() => setStep('verify')} style={{ marginBottom: 8 }}>
                I've added the account — Continue
              </button>
            </div>
          )}

          {step === 'verify' && (
            <form onSubmit={handleVerify}>
              <p style={{ fontSize: 13, color: '#374151', marginBottom: 16 }}>
                Enter the 6-digit code from your authenticator app to confirm setup:
              </p>
              <div className="login-field">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  className="login-input"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="000000"
                  autoFocus
                  maxLength={6}
                  style={{ letterSpacing: '0.3em', fontSize: 22, textAlign: 'center' }}
                  disabled={loading}
                />
              </div>
              <button type="submit" className="login-button" disabled={loading}>
                {loading ? 'Verifying...' : 'Confirm & Enable 2FA'}
              </button>
              <button type="button" onClick={() => setStep('scan')}
                style={{ display: 'block', margin: '8px auto 0', fontSize: 12, color: '#6C757D', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                ← Back
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default SecuritySetupPage;
